import { useState } from "react";
import { X, Plus, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { ImageUpload } from "../../components/ImageUpload";

const DIFFICULTIES = ["5th Grader", "Chess Club", "Full Pocket Protector"];

const EMPTY = {
  slug: "", comment_word: "", title: "", category: "", difficulty: "5th Grader",
  short_desc: "", header_image: "", versions: [], video_url: "",
  seo_title: "", seo_description: "", og_image: "", featured: false, start_order: "",
};

const newVersion = (difficulty) => ({
  difficulty, image: "", description: "", prompt: "", steps: [""], tiktok_url: "", youtube_url: "",
});

const slugify = (s) => s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

export function GuideEditor({ initial, onClose, onSaved }) {
  const isEdit = Boolean(initial);
  const [form, setForm] = useState(
    initial
      ? { ...EMPTY, ...initial, versions: (initial.versions && initial.versions.length)
          ? initial.versions.map((v) => ({ ...newVersion(v.difficulty), ...v, steps: v.steps?.length ? v.steps : [""] }))
          : [newVersion("5th Grader")] }
      : { ...EMPTY, versions: [newVersion("5th Grader")] }
  );
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setVer = (i, k, v) => setForm((f) => {
    const versions = [...f.versions];
    versions[i] = { ...versions[i], [k]: v };
    return { ...f, versions };
  });

  const usedTiers = form.versions.map((v) => v.difficulty);
  const addVersion = () => {
    const next = DIFFICULTIES.find((d) => !usedTiers.includes(d)) || "5th Grader";
    set("versions", [...form.versions, newVersion(next)]);
  };

  const save = async (e) => {
    e.preventDefault();
    if (form.versions.length === 0) {
      toast.error("Add at least one Nerd level version.");
      return;
    }
    setSaving(true);
    const payload = {
      slug: form.slug || slugify(form.title),
      comment_word: form.comment_word.toUpperCase(),
      title: form.title,
      category: form.category,
      difficulty: form.versions[0]?.difficulty || form.difficulty,
      short_desc: form.short_desc,
      header_image: form.header_image,
      prompt: "",
      steps: [],
      video_url: "",
      versions: form.versions.map((v) => ({
        difficulty: v.difficulty,
        image: v.image,
        description: v.description,
        prompt: v.prompt,
        steps: v.steps.map((s) => s.trim()).filter(Boolean),
        tiktok_url: v.tiktok_url,
        youtube_url: v.youtube_url,
      })),
      seo_title: form.seo_title,
      seo_description: form.seo_description,
      og_image: form.og_image,
      featured: form.featured,
      start_order: form.start_order === "" || form.start_order === null ? null : Number(form.start_order),
    };
    try {
      if (isEdit) await api.put(`/admin/guides/${initial.slug}`, payload);
      else await api.post("/admin/guides", payload);
      toast.success(isEdit ? "Setup updated" : "Setup published");
      onSaved();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Could not save.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center overflow-y-auto bg-navy/60 p-4 py-10" data-testid="guide-editor">
      <div className="brand-card w-full max-w-3xl p-7">
        <div className="mb-6 flex items-center justify-between">
          <h2 className="text-2xl">{isEdit ? "Edit app" : "New app"}</h2>
          <button onClick={onClose} className="text-text-muted hover:text-navy" data-testid="editor-close"><X size={22} /></button>
        </div>

        <form onSubmit={save} className="space-y-6">
          {/* Basics */}
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="field-label">App name (title)</label>
              <input className="brand-input" value={form.title} onChange={(e) => set("title", e.target.value)} required data-testid="editor-title" />
            </div>
            <div>
              <label className="field-label">Comment word</label>
              <input className="brand-input uppercase" value={form.comment_word} onChange={(e) => set("comment_word", e.target.value)} placeholder="CALM" required data-testid="editor-word" />
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="field-label">Slug (URL)</label>
              <input className="brand-input" value={form.slug} onChange={(e) => set("slug", e.target.value)} placeholder={slugify(form.title) || "auto-from-title"} data-testid="editor-slug" />
            </div>
            <div>
              <label className="field-label">Category</label>
              <input className="brand-input" value={form.category} onChange={(e) => set("category", e.target.value)} placeholder="Wellbeing" required data-testid="editor-category" />
            </div>
          </div>
          <div>
            <label className="field-label">One-line description</label>
            <input className="brand-input" value={form.short_desc} onChange={(e) => set("short_desc", e.target.value)} required data-testid="editor-desc" />
          </div>

          <ImageUpload label="Header image (app picture)" value={form.header_image} onChange={(v) => set("header_image", v)} testid="editor-header-image" />

          {/* Versions */}
          <div className="rounded-card border border-navy/12 bg-warm/50 p-5">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <p className="eyebrow">Nerd level versions</p>
                <p className="mt-1 text-[13px] text-text-muted">Add 1–3 tiers. Each shows on the page with its own prompt, picture and videos.</p>
              </div>
              <button type="button" onClick={addVersion} disabled={form.versions.length >= 3} className="btn btn-secondary !py-2 !px-4 text-[13px] disabled:opacity-40" data-testid="editor-add-version">
                <Plus size={15} /> Add level
              </button>
            </div>

            <div className="space-y-5">
              {form.versions.map((v, i) => (
                <div key={i} className="rounded-control border border-navy/15 bg-paper p-5" data-testid={`version-${i}`}>
                  <div className="mb-4 flex items-center justify-between">
                    <select className="brand-input font-sans !w-auto" value={v.difficulty} onChange={(e) => setVer(i, "difficulty", e.target.value)} data-testid={`version-${i}-difficulty`}>
                      {DIFFICULTIES.map((d) => <option key={d} value={d}>{d}</option>)}
                    </select>
                    <button type="button" onClick={() => set("versions", form.versions.filter((_, j) => j !== i))} className="text-brick hover:opacity-70" data-testid={`version-${i}-remove`}><Trash2 size={18} /></button>
                  </div>

                  <div className="grid gap-4 md:grid-cols-[auto_1fr]">
                    <ImageUpload label="Tier app picture" value={v.image} onChange={(url) => setVer(i, "image", url)} testid={`version-${i}-image`} aspect="aspect-square" />
                    <div className="space-y-4">
                      <div>
                        <label className="field-label">Short description</label>
                        <input className="brand-input" value={v.description} onChange={(e) => setVer(i, "description", e.target.value)} placeholder="What this version does" data-testid={`version-${i}-description`} />
                      </div>
                      <div className="grid gap-4 sm:grid-cols-2">
                        <div>
                          <label className="field-label">TikTok video URL</label>
                          <input className="brand-input" value={v.tiktok_url} onChange={(e) => setVer(i, "tiktok_url", e.target.value)} placeholder="https://tiktok.com/..." data-testid={`version-${i}-tiktok`} />
                        </div>
                        <div>
                          <label className="field-label">YouTube video URL</label>
                          <input className="brand-input" value={v.youtube_url} onChange={(e) => setVer(i, "youtube_url", e.target.value)} placeholder="https://youtube.com/..." data-testid={`version-${i}-youtube`} />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-4">
                    <label className="field-label">The prompt (copy-paste)</label>
                    <textarea className="brand-input min-h-[110px] font-serif" value={v.prompt} onChange={(e) => setVer(i, "prompt", e.target.value)} placeholder="Paste the exact prompt for this level" data-testid={`version-${i}-prompt`} />
                  </div>

                  <div className="mt-4">
                    <label className="field-label">How to use it (steps)</label>
                    <div className="space-y-2">
                      {v.steps.map((s, si) => (
                        <div key={si} className="flex gap-2">
                          <input className="brand-input" value={s} onChange={(e) => { const st = [...v.steps]; st[si] = e.target.value; setVer(i, "steps", st); }} placeholder={`Step ${si + 1}`} data-testid={`version-${i}-step-${si}`} />
                          <button type="button" onClick={() => setVer(i, "steps", v.steps.filter((_, j) => j !== si))} className="text-brick hover:opacity-70"><Trash2 size={16} /></button>
                        </div>
                      ))}
                      <button type="button" onClick={() => setVer(i, "steps", [...v.steps, ""])} className="inline-flex items-center gap-1 font-sans text-[12px] font-bold uppercase tracking-[0.12em] text-teal"><Plus size={14} /> Add step</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* SEO + placement */}
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="field-label">SEO title</label>
              <input className="brand-input" value={form.seo_title} onChange={(e) => set("seo_title", e.target.value)} data-testid="editor-seo-title" />
            </div>
            <div>
              <label className="field-label">Start Here order (blank = hide)</label>
              <input type="number" className="brand-input" value={form.start_order ?? ""} onChange={(e) => set("start_order", e.target.value)} placeholder="e.g. 1" data-testid="editor-start-order" />
            </div>
          </div>
          <div>
            <label className="field-label">SEO description</label>
            <input className="brand-input" value={form.seo_description} onChange={(e) => set("seo_description", e.target.value)} data-testid="editor-seo-desc" />
          </div>
          <label className="flex items-center gap-2 text-[15px] text-text-body">
            <input type="checkbox" checked={form.featured} onChange={(e) => set("featured", e.target.checked)} data-testid="editor-featured" />
            Feature on the homepage
          </label>

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn btn-secondary">Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving} data-testid="editor-save">
              {saving ? <Loader2 className="animate-spin" size={16} /> : isEdit ? "Save changes" : "Publish app"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
